import { t } from "@lingui/macro"
import { GridRenderCellParams } from "@mui/x-data-grid-pro"
import { useCallback, useMemo } from "react"

import { AccessTypeHeader } from "src/components/AccessTypeHeader"
import { NoWrap } from "src/components/NoWrap"
import { truncateText } from "src/lib/text"
import { IColumn } from "src/types/data-grid-pro"
import { AlertStatusChip } from "src/views/alerts/components/AlertStatusChip"
import { AlertLevelChip } from "src/views/alerts/components/AlertLevelChip"
import { IAlertListItem } from "src/views/alerts/types"

export const useGetAlertsColumns = (): IColumn<IAlertListItem>[] => {
    const renderStatusCell = useCallback(
        (params: GridRenderCellParams) => (
            <AlertStatusChip status={params.row.status} />
        ),
        [],
    )

    const renderTitleCell = useCallback(
        (params: GridRenderCellParams) => (
            <NoWrap title={params.row.title}>
                {truncateText(params.row.title ?? "", 30)}
            </NoWrap>
        ),
        [],
    )

    const renderMessageCell = useCallback(
        (params: GridRenderCellParams) => (
            <NoWrap title={params.row.text}>
                {truncateText(params.row.text ?? "", 30)}
            </NoWrap>
        ),
        [],
    )

    const renderAccessTypeCell = useCallback(
        (params: GridRenderCellParams) => (
            <AccessTypeHeader accessType={params.value} />
        ),
        [],
    )

    const renderLevelCell = useCallback(
        (params: GridRenderCellParams) => (
            <AlertLevelChip level={params.row.level} />
        ),
        [],
    )

    return useMemo<IColumn<IAlertListItem>[]>(
        () => [
            {
                field: "id",
                headerName: t`alerts-view.id-header`,
                width: 70,
                sortable: false,
                filterable: false,
            },
            {
                field: "status",
                headerName: t`alerts-view.status-header`,
                width: 120,
                renderCell: renderStatusCell,
            },
            {
                field: "level",
                headerName: t`alerts-view.level-header`,
                width: 120,
                renderCell: renderLevelCell,
            },
            {
                field: "title",
                headerName: t`alerts-view.title-header`,
                flex: 1,
                renderCell: renderTitleCell,
            },
            {
                field: "text",
                headerName: t`alerts-view.message-header`,
                flex: 1,
                renderCell: renderMessageCell,
            },
            {
                field: "segment_ids",
                headerName: t`alerts-view.visible-for-header`,
                width: 150,
                sortable: false,
                filterable: false,
                valueGetter: (params) => params.row.segment_ids?.length ?? 0,
            },
            {
                field: "access_type",
                headerName: t`alerts-view.access-header`,
                width: 150,
                sortable: false,
                filterable: false,
                renderCell: renderAccessTypeCell,
            },
        ],
        [
            renderStatusCell,
            renderLevelCell,
            renderTitleCell,
            renderMessageCell,
            renderAccessTypeCell,
        ],
    )
}
